import { useMemo, useState } from "react";
import { Modal } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Typography,
  TextField,
  Box,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
  Checkbox,
  ListItemText,
  ListSubheader,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ReactivateAdmin = ({
  showModal,
  setShowModal,
  user,
  roles = [],
  permissions = [],
  onSubmit,
  submitting = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const groupedPermissions = useMemo(() => {
    if (!permissions) return {};
    return permissions.reduce((acc, permission) => {
      const resource = permission.resource || "general";
      if (!acc[resource]) acc[resource] = [];
      acc[resource].push(permission);
      return acc;
    }, {});
  }, [permissions]);

  const initialRoles = useMemo(
    () => (user?.roles || []).map((r) => (typeof r === "string" ? r : r.name)),
    [user],
  );
  const initialPermissions = useMemo(
    () =>
      (user?.permissions || []).map((p) => (typeof p === "string" ? p : p.name)),
    [user],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      roles: initialRoles,
      permissions: initialPermissions,
      newPassword: "",
    },
    validationSchema: Yup.object({
      roles: Yup.array().min(1, "At least one role is required"),
      permissions: Yup.array(),
      newPassword: Yup.string()
        .min(8, "Password should be of minimum 8 characters length")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/\d/, "Password must contain at least one number")
        .matches(
          /[^a-zA-Z0-9]/,
          "Password must contain at least one special character",
        )
        .required("New password is required"),
    }),
    onSubmit: (values) => {
      onSubmit({
        roles: values.roles,
        permissions: values.permissions,
        newPassword: values.newPassword,
      });
    },
  });

  return (
    <Modal
      open={showModal}
      closable={false}
      onCancel={() => setShowModal(false)}
      footer={null}
    >
      <Typography
        variant="h4"
        gutterBottom
        style={{ textAlign: "center", marginBottom: "8px" }}
      >
        Reactivate Admin User
      </Typography>
      {user ? (
        <Typography
          variant="body2"
          style={{ textAlign: "center", marginBottom: "24px", color: "#666" }}
        >
          {user.fullName ||
            `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()}
          {user.email ? ` (${user.email})` : ""}
        </Typography>
      ) : null}

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="medium">
              Roles
            </Typography>
            <TextField
              select
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) =>
                  selected.length ? selected.join(", ") : "Please select roles",
              }}
              fullWidth
              name="roles"
              value={formik.values.roles || []}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="small"
              error={formik.touched.roles && Boolean(formik.errors.roles)}
              helperText={formik.touched.roles && formik.errors.roles}
            >
              {roles.map((role) => {
                const value = typeof role === "string" ? role : role.name;
                return (
                  <MenuItem key={value} value={value}>
                    <Checkbox
                      checked={(formik.values.roles || []).includes(value)}
                    />
                    <ListItemText primary={value} />
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="medium">
              Permissions
            </Typography>
            <TextField
              select
              fullWidth
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#333",
                    }}
                  >
                    {selected.length
                      ? selected.join(", ")
                      : "Please select permissions"}
                  </span>
                ),
              }}
              name="permissions"
              value={formik.values.permissions || []}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="small"
            >
              {Object.entries(groupedPermissions).map(([resource, perms]) => [
                <ListSubheader key={resource}>
                  {resource.toUpperCase()}
                </ListSubheader>,
                perms.map((permission) => (
                  <MenuItem key={permission.id} value={permission.name}>
                    <Checkbox
                      checked={(formik.values.permissions || []).includes(
                        permission.name,
                      )}
                    />
                    <ListItemText primary={permission.name} />
                  </MenuItem>
                )),
              ])}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="medium">
              New Password
            </Typography>
            <TextField
              size="small"
              fullWidth
              type={showPassword ? "text" : "password"}
              name="newPassword"
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.newPassword &&
                Boolean(formik.errors.newPassword)
              }
              helperText={
                formik.touched.newPassword && formik.errors.newPassword
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Box
          margin={3}
          display={"flex"}
          justifyContent={"center"}
          gap={2}
        >
          <Button
            variant="outlined"
            onClick={() => setShowModal(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={submitting}>
            {submitting ? "Reactivating..." : "Reactivate User"}
          </Button>
        </Box>
      </form>
    </Modal>
  );
};

export default ReactivateAdmin;
